import React from "react";
import { Breadcrumb, Layout, theme, Spin, Alert } from "antd";
import { useAuth } from "../hooks/useAuth";
import { useIntl } from "react-intl";
import { useParams, Link } from "react-router-dom";
import { useGetBrandsQuery } from "../services/nestjs-base-api";
import BrandDetails from "../components/Brand/BrandDetails";
import DataQualitySummary from "../components/Brand/DataQualitySummary";

const { Content } = Layout;

const BrandDetailsPage: React.FunctionComponent = () => {
    const { user } = useAuth();
    const intl = useIntl();
    const { brandHash } = useParams<{ brandHash: string }>();
    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    // Fetch all brands to find the one with matching hash
    const { 
        data: brands, 
        isLoading, 
        error 
    } = useGetBrandsQuery({
        jwtToken: user?.jwtToken || "",
        organisationId: user?.organisation?.id || 0
    }, {
        skip: !user?.jwtToken || !user?.organisation?.id
    });

    // Find the brand with matching hash
    const brand = brands?.find(brand => brand.hash === brandHash);

    if (isLoading) {
        return (
            <Content style={{ margin: '0 16px' }}>
                <div style={{ 
                    padding: 24, 
                    minHeight: '82vh',
                    background: colorBgContainer,
                    borderRadius: borderRadiusLG,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <Spin size="large" />
                </div>
            </Content>
        );
    }

    if (error || !brand) {
        return (
            <Content style={{ margin: '0 16px' }}>
                <Breadcrumb 
                    style={{ margin: '16px 0' }}
                    items={[
                        { 
                            title: (
                                <Link to="/dashboard/brands">
                                    {intl.formatMessage({ id: 'section.brands' })}
                                </Link>
                            )
                        },
                        { title: 'Brand Details' }
                    ]}
                />
                
                <div style={{ 
                    padding: 24,
                    minHeight: '82vh',
                    background: colorBgContainer,
                    borderRadius: borderRadiusLG,
                }}>
                    <Alert 
                        type="error" 
                        message="Brand Not Found" 
                        description="The brand you're looking for doesn't exist or you don't have permission to view it." 
                        action={
                            <Link to="/dashboard/brands">
                                Back to Brands
                            </Link>
                        }
                    />
                </div>
            </Content>
        );
    }

    return (
        <Content style={{ margin: '0 16px' }}>
            <Breadcrumb 
                style={{ margin: '16px 0' }}
                items={[
                    { title: user.organisation?.name || intl.formatMessage({ id: 'common.platformName' }) },
                    { 
                        title: (
                            <Link to="/dashboard/brands">
                                {intl.formatMessage({ id: 'section.brands' })}
                            </Link>
                        )
                    },
                    { title: brand.name }
                ]}
            />
            
            <div
                style={{
                    padding: 24,
                    minHeight: '82vh',
                    background: colorBgContainer,
                    borderRadius: borderRadiusLG,
                }}
            >
                <DataQualitySummary brandHash={brand.hash} />
                <BrandDetails brand={brand} />
            </div>
        </Content>
    );
}

export default BrandDetailsPage;